import { useState, useMemo, useRef, useCallback } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { ToastAction } from "@/components/ui/toast";
import { useIsMobile } from "@/hooks/use-mobile";
import { invalidateTaskQueries } from "@/lib/task-query-utils";
import { format } from "date-fns";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { Label } from "@/components/ui/label";
import {
  Search,
  Loader2,
  ChevronLeft,
  CalendarCheck,
  CheckSquare,
  Plus,
  FileText,
  AlertCircle,
  Bell,
} from "lucide-react";
import { AnimalAvatar } from "@/components/animals/animal-avatar";
import { CreateTaskForm } from "@/components/tasks/create-task-form";
import { getBoardRoutePath, getBoardLabel, getBoardEntityLabel } from "@/lib/board-types";

interface SearchTask {
  id: string;
  title: string;
  type: string;
  boardId: string;
  boardName: string;
  statusName: string | null;
  dueDate: string | null;
}

interface Board {
  id: string;
  name: string;
  type: string;
  isArchived: boolean;
  userRole: string;
}

interface Template {
  id: string;
  boardId: string;
  title: string;
  description: string | null;
  priority: string | null;
  type: string | null;
  labelIds: string[];
  createdAt: string;
}

interface BoardStatus {
  id: string;
  name: string;
  sortOrder: number;
  isDefault?: boolean;
  isArchived?: boolean;
}

interface BoardLabel {
  id: string;
  name: string;
  color: string | null;
  isArchived?: boolean;
}

interface BoardMember {
  userId: string;
  role: string;
  username: string;
  firstName: string | null;
  lastName: string | null;
}

type Step =
  | "picker"
  | "search_meeting"
  | "search_task"
  | "board_picker_meeting"
  | "board_picker_task"
  | "template_picker"
  | "add_comment"
  | "add_context_note"
  | "create_form"
  | "create_reminder_form";

function getContextPrompt(boardType: string): string {
  return getBoardLabel(boardType, "contextPrompt");
}

interface EntityLinkerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  entityType: "animal" | "interaction";
  entityId: string;
  entityLabel?: string;
  animalId?: string;
  animalSpecies?: string;
  animalPhotoUrl?: string | null;
  onTaskCreated?: (taskId: string) => void;
}

function getSpeciesLabel(species?: string): string {
  if (!species) return "OTHER";
  const upper = species.toUpperCase();
  if (upper === "DOG") return "DOG";
  if (upper === "CAT") return "CAT";
  return "OTHER";
}

export function EntityLinkerDialog({
  open,
  onOpenChange,
  entityType,
  entityId,
  entityLabel,
  animalId,
  animalSpecies,
  animalPhotoUrl,
  onTaskCreated,
}: EntityLinkerDialogProps) {
  const { toast } = useToast();
  const [, navigate] = useLocation();
  const isMobile = useIsMobile();

  const viewTaskAction = (taskId: string, taskType?: string) => (
    <ToastAction altText="View" onClick={() => navigate(`${getBoardRoutePath(taskType)}/${taskId}`)}>View</ToastAction>
  );

  const [step, setStep] = useState<Step>("picker");
  const [search, setSearch] = useState("");
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);
  const [selectedTaskTitle, setSelectedTaskTitle] = useState<string>("");
  const [comment, setComment] = useState("");
  const [selectedBoardId, setSelectedBoardId] = useState<string>("");
  const [selectedBoardType, setSelectedBoardType] = useState<string>("regular");
  const [contextNote, setContextNote] = useState("");
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
  const [selectedTemplateTitle, setSelectedTemplateTitle] = useState("");
  const [selectedTemplateDescription, setSelectedTemplateDescription] = useState("");
  const [selectedTemplatePriority, setSelectedTemplatePriority] = useState<string | null>(null);
  const [selectedTemplateLabelIds, setSelectedTemplateLabelIds] = useState<string[]>([]);
  const [formKey, setFormKey] = useState(0);
  const requestCloseRef = useRef<((action: "cancel" | "close") => void) | null>(null);

  const entityTypeLabel = entityType === "animal" ? "Animal" : "Interaction";
  const title = isMobile
    ? `+ Add to...`
    : `+ Add ${entityTypeLabel} to...`;

  const resetState = useCallback(() => {
    setStep("picker");
    setSearch("");
    setSelectedTaskId(null);
    setSelectedTaskTitle("");
    setComment("");
    setSelectedBoardId("");
    setSelectedBoardType("regular");
    setContextNote("");
    setSelectedTemplateId(null);
    setSelectedTemplateTitle("");
    setSelectedTemplateDescription("");
    setSelectedTemplatePriority(null);
    setSelectedTemplateLabelIds([]);
  }, []);

  const handleClose = useCallback((nextOpen: boolean) => {
    onOpenChange(nextOpen);
    if (!nextOpen) {
      resetState();
    }
  }, [onOpenChange, resetState]);

  const handleDialogOpenChange = useCallback((nextOpen: boolean) => {
    if (!nextOpen && (step === "create_form" || step === "create_reminder_form") && requestCloseRef.current) {
      requestCloseRef.current("close");
    } else {
      handleClose(nextOpen);
    }
  }, [step, handleClose]);

  const [searchOrigin, setSearchOrigin] = useState<"search_meeting" | "search_task">("search_task");

  const handleBack = () => {
    if (step === "add_comment") {
      setSelectedTaskId(null);
      setSelectedTaskTitle("");
      setComment("");
      setStep(searchOrigin);
      return;
    }
    if (step === "create_reminder_form") {
      setStep("picker");
      setFormKey(k => k + 1);
      return;
    }
    if (step === "create_form") {
      setStep("add_context_note");
      setFormKey(k => k + 1);
      return;
    }
    if (step === "add_context_note") {
      if (selectedTemplateId) {
        setStep("template_picker");
      } else {
        setStep(selectedBoardType === "meeting" ? "board_picker_meeting" : "board_picker_task");
      }
      return;
    }
    if (step === "template_picker") {
      setSelectedBoardId("");
      setStep("board_picker_meeting");
      return;
    }
    setStep("picker");
    setSearch("");
    setSelectedBoardId("");
    setSelectedBoardType("regular");
    setContextNote("");
    setSelectedTemplateId(null);
    setSelectedTemplateTitle("");
    setSelectedTemplateDescription("");
    setSelectedTemplatePriority(null);
    setSelectedTemplateLabelIds([]);
  };

  const excludeAnimalId = entityType === "animal" ? entityId : animalId;

  const searchType = step === "search_meeting" ? "meeting" : step === "search_task" ? "regular" : null;

  const taskSearchParams = new URLSearchParams();
  taskSearchParams.set("search", search);
  taskSearchParams.set("limit", "20");
  taskSearchParams.set("excludePastDates", "true");
  if (searchType) taskSearchParams.set("type", searchType);
  if (excludeAnimalId) taskSearchParams.set("excludeAnimalId", excludeAnimalId);

  const { data: searchTasksData, isLoading: searchTasksLoading } = useQuery<{ tasks: SearchTask[] }>({
    queryKey: ["/api/tasks/search", search, searchType, excludeAnimalId],
    queryFn: async () => {
      const res = await fetch(`/api/tasks/search?${taskSearchParams.toString()}`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to search tasks");
      return res.json();
    },
    enabled: open && (step === "search_meeting" || step === "search_task"),
  });

  const { data: boardsData } = useQuery<{ boards: Board[] }>({
    queryKey: ["/api/task-boards"],
    enabled: open && (step === "board_picker_meeting" || step === "board_picker_task"),
  });

  const filteredBoards = useMemo(() => {
    if (!boardsData?.boards) return [];
    const targetType = step === "board_picker_meeting" ? "meeting" : "regular";
    return boardsData.boards.filter(
      b => b.type === targetType && !b.isArchived
    );
  }, [boardsData, step]);

  const writableBoards = useMemo(() => {
    return filteredBoards.filter(b => b.userRole === "admin" || b.userRole === "editor");
  }, [filteredBoards]);

  const { data: templatesData, isLoading: templatesLoading } = useQuery<{ templates: Template[] }>({
    queryKey: ["/api/task-boards", selectedBoardId, "templates"],
    enabled: open && step === "template_picker" && !!selectedBoardId,
  });

  const { data: reminderBoardData } = useQuery<{ reminders: unknown[]; boardId: string }>({
    queryKey: ["/api/tasks/my-reminders"],
    enabled: open && step === "create_reminder_form",
  });

  const reminderBoardId = reminderBoardData?.boardId ?? "";

  const effectiveBoardId = step === "create_reminder_form" ? reminderBoardId : selectedBoardId;

  const { data: boardStatusesData } = useQuery<{ statuses: BoardStatus[] }>({
    queryKey: ["/api/task-boards", effectiveBoardId, "statuses"],
    enabled: open && !!effectiveBoardId && (step === "add_context_note" || step === "create_form" || step === "create_reminder_form"),
  });

  const { data: boardLabelsData } = useQuery<{ labels: BoardLabel[] }>({
    queryKey: ["/api/task-boards", effectiveBoardId, "labels"],
    enabled: open && !!effectiveBoardId && (step === "add_context_note" || step === "create_form" || step === "create_reminder_form"),
  });

  const { data: boardMembersData } = useQuery<{ members: BoardMember[] }>({
    queryKey: ["/api/tasks/board-members", effectiveBoardId],
    enabled: open && !!effectiveBoardId && (step === "add_context_note" || step === "create_form" || step === "create_reminder_form"),
  });

  const linkMutation = useMutation({
    mutationFn: async (taskId: string) => {
      if (entityType === "animal") {
        await apiRequest("POST", `/api/tasks/${taskId}/animals`, { animalId: entityId });
      } else {
        await apiRequest("POST", `/api/tasks/${taskId}/interactions`, { interactionId: entityId });
        if (animalId) {
          try {
            await apiRequest("POST", `/api/tasks/${taskId}/animals`, { animalId });
          } catch (_) {}
        }
      }
    },
    onSuccess: async (_data, taskId) => {
      const invalidateActivityFeed = () => {
        const targetAnimalId = entityType === "animal" ? entityId : animalId;
        if (targetAnimalId) {
          queryClient.invalidateQueries({ queryKey: ["/api/animals", targetAnimalId, "activity"] });
        }
      };
      if (comment.trim()) {
        try {
          await apiRequest("POST", `/api/notes/task/${taskId}/append`, {
            text: comment.trim(),
            sourceEntityType: entityType,
            sourceEntityLabel: entityLabel,
          });
        } catch (err: unknown) {
          const message = err instanceof Error ? err.message : "Notes may be locked by another user";
          toast({
            title: "Linked successfully, but comment was not added",
            description: message,
            variant: "destructive",
          });
          handleClose(false);
          invalidateTaskQueries();
          queryClient.invalidateQueries({ queryKey: ["/api/tasks/search"] });
          invalidateActivityFeed();
          return;
        }
      }
      const targetLabel = selectedTaskTitle || getBoardEntityLabel(searchOrigin === "search_meeting" ? "meeting" : "regular");
      const sourceLabel = entityLabel || entityTypeLabel;
      toast({ title: `${sourceLabel} linked to ${targetLabel}`, action: viewTaskAction(taskId, searchOrigin === "search_meeting" ? "meeting" : "regular") });
      handleClose(false);
      invalidateTaskQueries();
      queryClient.invalidateQueries({ queryKey: ["/api/tasks/search"] });
      invalidateActivityFeed();
    },
    onError: (error: Error) => {
      toast({
        title: `Failed to link ${entityType}`,
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const handleTaskSelect = (task: SearchTask) => {
    setSelectedTaskId(task.id);
    setSelectedTaskTitle(task.title);
    setSearchOrigin(step as "search_meeting" | "search_task");
    setStep("add_comment");
  };

  const handleConfirmLink = () => {
    if (selectedTaskId) {
      linkMutation.mutate(selectedTaskId);
    }
  };

  const handleBoardSelect = (boardId: string) => {
    setSelectedBoardId(boardId);
    if (step === "board_picker_meeting") {
      setSelectedBoardType("meeting");
      setStep("template_picker");
    } else {
      setSelectedBoardType("regular");
      setSelectedTemplateId(null);
      setSelectedTemplateTitle("");
      setSelectedTemplateDescription("");
      setSelectedTemplatePriority(null);
      setSelectedTemplateLabelIds([]);
      setStep("add_context_note");
    }
  };

  const handleTemplateSelect = (templateId: string) => {
    const templates = templatesData?.templates ?? [];
    const tmpl = templates.find(t => t.id === templateId);
    setSelectedTemplateId(templateId);
    setSelectedTemplateTitle(tmpl?.title ?? "");
    setSelectedTemplateDescription(tmpl?.description ?? "");
    setSelectedTemplatePriority(tmpl?.priority ?? null);
    setSelectedTemplateLabelIds(tmpl?.labelIds ?? []);
    setStep("add_context_note");
  };

  const handleContextNoteNext = () => {
    setFormKey(k => k + 1);
    setStep("create_form");
  };

  const handleContextNoteSkip = () => {
    setContextNote("");
    setFormKey(k => k + 1);
    setStep("create_form");
  };

  const handleFormClose = useCallback(() => {
    handleClose(false);
  }, [handleClose]);

  const handleFormCancel = useCallback(() => {
    setStep("add_context_note");
    setFormKey(k => k + 1);
  }, []);

  const initialAnimalIds = useMemo(() => {
    if (entityType === "animal") return [entityId];
    if (animalId) return [animalId];
    return [];
  }, [entityType, entityId, animalId]);

  const initialAnimalNames = useMemo(() => {
    const map = new Map<string, string>();
    if (entityType === "animal" && entityLabel) {
      map.set(entityId, entityLabel);
    }
    return map;
  }, [entityType, entityId, entityLabel]);

  const initialInteractionIds = useMemo(() => {
    if (entityType === "interaction") return [entityId];
    return [];
  }, [entityType, entityId]);

  const initialInteractionNames = useMemo(() => {
    const map = new Map<string, string>();
    if (entityType === "interaction" && entityLabel) {
      map.set(entityId, entityLabel);
    }
    return map;
  }, [entityType, entityId, entityLabel]);

  const hasWritableMeetingBoards = useMemo(() => {
    if (!boardsData?.boards) return true;
    return boardsData.boards.some(
      b => b.type === "meeting" && !b.isArchived && (b.userRole === "admin" || b.userRole === "editor")
    );
  }, [boardsData]);

  const hasWritableTaskBoards = useMemo(() => {
    if (!boardsData?.boards) return true;
    return boardsData.boards.some(
      b => b.type === "regular" && !b.isArchived && (b.userRole === "admin" || b.userRole === "editor")
    );
  }, [boardsData]);

  const renderContent = () => {
    if (step === "picker") {
      return renderPicker();
    }
    if (step === "search_meeting" || step === "search_task") {
      return renderSearch();
    }
    if (step === "add_comment") {
      return renderAddComment();
    }
    if (step === "board_picker_meeting" || step === "board_picker_task") {
      return renderBoardPicker();
    }
    if (step === "template_picker") {
      return renderTemplatePicker();
    }
    if (step === "add_context_note") {
      return renderContextNote();
    }
    if (step === "create_form") {
      return renderCreateForm();
    }
    if (step === "create_reminder_form") {
      return renderReminderForm();
    }
    return null;
  };

  const effectiveAnimalId = entityType === "animal" ? entityId : animalId;

  const renderEntityInfo = () => {
    if (!entityLabel) return null;
    if (entityType === "animal" && effectiveAnimalId) {
      return (
        <div className="flex items-center gap-2 mt-1" data-testid="text-entity-linker-label">
          <AnimalAvatar
            animalId={effectiveAnimalId}
            photoUrl={animalPhotoUrl ?? null}
            name={entityLabel}
            species={animalSpecies || ""}
            className="h-7 w-7"
          />
          <span className="text-sm text-muted-foreground truncate">{entityLabel}</span>
          {animalSpecies && (
            <Badge variant="secondary" className="text-xs shrink-0" data-testid="badge-entity-species">
              {getSpeciesLabel(animalSpecies)}
            </Badge>
          )}
        </div>
      );
    }
    return (
      <DialogDescription data-testid="text-entity-linker-label">{entityLabel}</DialogDescription>
    );
  };

  const renderPicker = () => (
    <>
      <DialogHeader>
        <DialogTitle data-testid="text-entity-linker-title">{title}</DialogTitle>
        {renderEntityInfo()}
      </DialogHeader>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-2" data-testid="entity-linker-picker">
        <div className="space-y-2">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Meetings</p>
          <button
            className="w-full text-left p-3 rounded-md border hover-elevate flex items-start gap-3"
            onClick={() => setStep("search_meeting")}
            data-testid="button-add-to-existing-meeting"
          >
            <CalendarCheck className="w-5 h-5 mt-0.5 text-muted-foreground shrink-0" />
            <div className="min-w-0">
              <div className="font-medium text-sm">Existing meeting</div>
              <div className="text-xs text-muted-foreground mt-0.5">Search meeting agendas</div>
            </div>
          </button>
          <button
            className="w-full text-left p-3 rounded-md border hover-elevate flex items-start gap-3"
            onClick={() => setStep("board_picker_meeting")}
            data-testid="button-create-new-meeting"
          >
            <Plus className="w-5 h-5 mt-0.5 text-muted-foreground shrink-0" />
            <div className="min-w-0">
              <div className="font-medium text-sm">New meeting</div>
              <div className="text-xs text-muted-foreground mt-0.5">Create from template</div>
            </div>
          </button>
        </div>
        <div className="space-y-2">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Tasks</p>
          <button
            className="w-full text-left p-3 rounded-md border hover-elevate flex items-start gap-3"
            onClick={() => setStep("search_task")}
            data-testid="button-add-to-existing-task"
          >
            <CheckSquare className="w-5 h-5 mt-0.5 text-muted-foreground shrink-0" />
            <div className="min-w-0">
              <div className="font-medium text-sm">Existing task</div>
              <div className="text-xs text-muted-foreground mt-0.5">Search tasks</div>
            </div>
          </button>
          <button
            className="w-full text-left p-3 rounded-md border hover-elevate flex items-start gap-3"
            onClick={() => setStep("board_picker_task")}
            data-testid="button-create-new-task"
          >
            <Plus className="w-5 h-5 mt-0.5 text-muted-foreground shrink-0" />
            <div className="min-w-0">
              <div className="font-medium text-sm">New task</div>
              <div className="text-xs text-muted-foreground mt-0.5">Start a new task</div>
            </div>
          </button>
        </div>
        <div className="space-y-2">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Reminders</p>
          <button
            className="w-full text-left p-3 rounded-md border hover-elevate flex items-start gap-3"
            onClick={() => {
              setSelectedBoardType("reminder");
              setFormKey(k => k + 1);
              setStep("create_reminder_form");
            }}
            data-testid="button-create-new-reminder"
          >
            <Bell className="w-5 h-5 mt-0.5 text-muted-foreground shrink-0" />
            <div className="min-w-0">
              <div className="font-medium text-sm">New reminder</div>
              <div className="text-xs text-muted-foreground mt-0.5">Set a personal reminder</div>
            </div>
          </button>
        </div>
      </div>
    </>
  );

  const renderSearch = () => {
    const searchBoardType = step === "search_meeting" ? "meeting" : "regular";
    const searchLabel = getBoardLabel(searchBoardType, "searchPlaceholder");
    const titleLabel = getBoardLabel(searchBoardType, "addToExisting");

    return (
      <>
        <DialogHeader>
          <div className="flex items-center gap-2">
            <button
              onClick={handleBack}
              className="text-muted-foreground hover:text-foreground transition-colors"
              data-testid="button-back-to-picker"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <DialogTitle data-testid="text-search-title">{titleLabel}</DialogTitle>
          </div>
        </DialogHeader>
        <div className="space-y-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder={searchLabel}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
              autoFocus
              data-testid="input-entity-linker-search"
            />
          </div>
          <div className="max-h-64 min-h-[12rem] overflow-y-auto space-y-1">
            {searchTasksLoading && (
              <div className="flex items-center justify-center py-6">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              </div>
            )}
            {!searchTasksLoading && searchTasksData?.tasks?.length === 0 && (
              <p className="text-sm text-muted-foreground text-center py-6" data-testid="text-no-results">
                {search ? "No results found" : `Type to search ${getBoardLabel(searchBoardType, "plural").toLowerCase()}`}
              </p>
            )}
            {!searchTasksLoading && searchTasksData?.tasks?.map((task) => (
              <button
                key={task.id}
                onClick={() => handleTaskSelect(task)}
                className="w-full text-left p-2 rounded-md hover-elevate flex items-start gap-3"
                data-testid={`button-select-task-${task.id}`}
              >
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{task.title}</p>
                  <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 mt-0.5">
                    <span className="text-xs text-muted-foreground">{task.boardName}</span>
                    {task.statusName && (
                      <Badge variant="secondary" className="text-xs">{task.statusName}</Badge>
                    )}
                    {task.dueDate && (
                      <span className="text-xs text-muted-foreground">{getBoardLabel(searchBoardType, "dueDateShort")} {format(new Date(task.dueDate), "EEE, MMM d")}</span>
                    )}
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      </>
    );
  };

  const renderAddComment = () => (
    <>
      <DialogHeader>
        <div className="flex items-center gap-2">
          <button
            onClick={handleBack}
            className="text-muted-foreground hover:text-foreground transition-colors"
            data-testid="button-back-from-comment"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <DialogTitle data-testid="text-comment-title">Add a Note</DialogTitle>
        </div>
        <DialogDescription>
          Linking to <span className="font-medium text-foreground">{selectedTaskTitle}</span>
        </DialogDescription>
      </DialogHeader>
      <div className="space-y-3">
        <Textarea
          placeholder="Add an optional note about why this is being linked..."
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          className="resize-none text-sm"
          rows={3}
          data-testid="input-link-comment"
        />
        <div className="flex justify-end gap-2">
          <Button
            variant="outline"
            onClick={() => {
              setComment("");
              handleConfirmLink();
            }}
            disabled={linkMutation.isPending}
            data-testid="button-link-skip-comment"
          >
            Skip
          </Button>
          <Button
            onClick={handleConfirmLink}
            disabled={linkMutation.isPending}
            data-testid="button-link-with-comment"
          >
            {linkMutation.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : null}
            {comment.trim() ? "Link with Note" : "Link"}
          </Button>
        </div>
      </div>
    </>
  );

  const renderBoardPicker = () => {
    const pickerBoardType = step === "board_picker_meeting" ? "meeting" : "regular";
    const titleLabel = getBoardLabel(pickerBoardType, "boardPickerTitle");

    return (
      <>
        <DialogHeader>
          <div className="flex items-center gap-2">
            <button
              onClick={handleBack}
              className="text-muted-foreground hover:text-foreground transition-colors"
              data-testid="button-back-to-picker-from-boards"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <DialogTitle data-testid="text-board-picker-title">{titleLabel}</DialogTitle>
          </div>
          <DialogDescription>
            Choose which board to create on.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2 mt-2" data-testid="entity-linker-board-list">
          {!boardsData && (
            <div className="space-y-2">
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
            </div>
          )}
          {filteredBoards.length === 0 && boardsData && (
            <div className="flex flex-col items-center gap-3 py-6 text-center">
              <AlertCircle className="w-10 h-10 text-muted-foreground" />
              <p className="text-sm text-muted-foreground">
                No {getBoardLabel(pickerBoardType, "entityLower")} boards available. Contact an admin for access.
              </p>
            </div>
          )}
          {filteredBoards.map(board => {
            const canWrite = board.userRole === "admin" || board.userRole === "editor";
            return (
              <button
                key={board.id}
                className={`w-full text-left p-3 rounded-md border flex items-center justify-between gap-3 ${canWrite ? "hover-elevate cursor-pointer" : "opacity-50 cursor-not-allowed"}`}
                onClick={() => canWrite && handleBoardSelect(board.id)}
                disabled={!canWrite}
                data-testid={`button-board-${board.id}`}
              >
                <span className="font-medium text-sm truncate">{board.name}</span>
                {!canWrite && (
                  <Badge variant="secondary" className="text-xs shrink-0">View Only</Badge>
                )}
              </button>
            );
          })}
        </div>
      </>
    );
  };

  const renderTemplatePicker = () => {
    const templates = templatesData?.templates ?? [];

    return (
      <>
        <DialogHeader>
          <div className="flex items-center gap-2">
            <button
              onClick={handleBack}
              className="text-muted-foreground hover:text-foreground transition-colors"
              data-testid="button-back-to-board-picker"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <DialogTitle data-testid="text-template-picker-title">Select a Template</DialogTitle>
          </div>
          <DialogDescription>Choose a template to create the meeting from.</DialogDescription>
        </DialogHeader>
        <div className="space-y-2 mt-2" data-testid="entity-linker-template-list">
          {templatesLoading && (
            <div className="space-y-2">
              <Skeleton className="h-16 w-full" />
              <Skeleton className="h-16 w-full" />
            </div>
          )}
          {!templatesLoading && templates.length === 0 && (
            <div className="flex flex-col items-center gap-3 py-6 text-center">
              <AlertCircle className="w-10 h-10 text-muted-foreground" />
              <p className="text-sm text-muted-foreground">
                No templates available for this board. Ask a board admin to create one.
              </p>
            </div>
          )}
          {!templatesLoading && templates.map(t => (
            <button
              key={t.id}
              className="w-full text-left p-3 rounded-md border hover-elevate flex items-start gap-3"
              onClick={() => handleTemplateSelect(t.id)}
              data-testid={`button-template-${t.id}`}
            >
              <FileText className="w-5 h-5 mt-0.5 text-muted-foreground shrink-0" />
              <div className="min-w-0">
                <div className="font-medium text-sm">{t.title}</div>
                {t.description && (
                  <div className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{t.description}</div>
                )}
              </div>
            </button>
          ))}
        </div>
      </>
    );
  };

  const renderContextNote = () => {
    const promptBase = getContextPrompt(selectedBoardType);
    const entityName = entityLabel || (entityType === "animal" ? "this animal" : "this interaction");
    const prompt = `${promptBase} ${entityName}?`;
    const boardTypeLabel = getBoardEntityLabel(selectedBoardType).toLowerCase();

    return (
      <>
        <DialogHeader>
          <div className="flex items-center gap-2">
            <button
              onClick={handleBack}
              className="text-muted-foreground hover:text-foreground transition-colors"
              data-testid="button-back-from-context-note"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <DialogTitle data-testid="text-context-note-title">Add Context</DialogTitle>
          </div>
          <DialogDescription data-testid="text-context-note-prompt">{prompt}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <Textarea
            placeholder={`Optional note for this ${boardTypeLabel}...`}
            value={contextNote}
            onChange={(e) => setContextNote(e.target.value)}
            className="resize-none text-sm"
            rows={3}
            autoFocus
            data-testid="input-context-note"
          />
          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={handleContextNoteSkip}
              data-testid="button-context-note-skip"
            >
              Skip
            </Button>
            <Button
              onClick={handleContextNoteNext}
              data-testid="button-context-note-next"
            >
              Next
            </Button>
          </div>
        </div>
      </>
    );
  };

  const renderCreateForm = () => {
    const boardTypeLabel = getBoardEntityLabel(selectedBoardType);
    const activeStatuses = (boardStatusesData?.statuses ?? []).filter((s) => !s.isArchived);
    const activeLabels = (boardLabelsData?.labels ?? []).filter((l) => !l.isArchived);

    return (
      <>
        <DialogHeader>
          <div className="flex items-center gap-2">
            <button
              onClick={handleBack}
              className="text-muted-foreground hover:text-foreground transition-colors"
              data-testid="button-back-from-create-form"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <DialogTitle data-testid="text-create-form-title">New {boardTypeLabel}</DialogTitle>
          </div>
        </DialogHeader>
        <div className="max-h-[60vh] overflow-y-auto mt-2">
          <CreateTaskForm
            key={formKey}
            boardId={selectedBoardId}
            statuses={activeStatuses}
            labels={activeLabels}
            members={boardMembersData?.members ?? []}
            boardType={selectedBoardType}
            initialTitle={selectedTemplateTitle}
            initialDescription={selectedTemplateDescription}
            initialPriority={selectedTemplatePriority}
            initialLabelIds={selectedTemplateLabelIds}
            templateId={selectedTemplateId ?? undefined}
            initialNote={contextNote}
            showNote={true}
            initialAnimalIds={initialAnimalIds}
            initialAnimalNames={initialAnimalNames}
            initialInteractionIds={initialInteractionIds}
            initialInteractionNames={initialInteractionNames}
            onClose={handleFormClose}
            onCancel={handleFormCancel}
            onRequestCloseRef={requestCloseRef}
            compact={true}
            active={open && step === "create_form"}
          />
        </div>
      </>
    );
  };

  const renderReminderForm = () => {
    if (!reminderBoardId) {
      return (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </div>
      );
    }
    const activeStatuses = (boardStatusesData?.statuses ?? []).filter((s) => !s.isArchived);
    const activeLabels = (boardLabelsData?.labels ?? []).filter((l) => !l.isArchived);

    return (
      <>
        <DialogHeader>
          <div className="flex items-center gap-2">
            <button
              onClick={handleBack}
              className="text-muted-foreground hover:text-foreground transition-colors"
              data-testid="button-back-from-reminder-form"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <DialogTitle data-testid="text-reminder-form-title">New Reminder</DialogTitle>
          </div>
        </DialogHeader>
        <div className="max-h-[60vh] overflow-y-auto mt-2">
          <CreateTaskForm
            key={formKey}
            boardId={reminderBoardId}
            statuses={activeStatuses}
            labels={activeLabels}
            members={boardMembersData?.members ?? []}
            boardType="reminder"
            showNote={true}
            initialAnimalIds={initialAnimalIds}
            initialAnimalNames={initialAnimalNames}
            initialInteractionIds={initialInteractionIds}
            initialInteractionNames={initialInteractionNames}
            onClose={handleFormClose}
            onCancel={() => {
              setStep("picker");
              setFormKey(k => k + 1);
            }}
            onRequestCloseRef={requestCloseRef}
            compact={true}
            active={open && step === "create_reminder_form"}
          />
        </div>
      </>
    );
  };

  return (
    <Dialog open={open} onOpenChange={handleDialogOpenChange}>
      <DialogContent className={(step === "create_form") ? "sm:max-w-md" : step === "create_reminder_form" ? "sm:max-w-lg" : step === "picker" ? "sm:max-w-2xl" : "sm:max-w-lg"} data-testid="dialog-entity-linker">
        {renderContent()}
      </DialogContent>
    </Dialog>
  );
}
